import {
  absoluteUrl,
  getOrganizationSchemaId,
  getSiteUrl,
  getWebsiteSchemaId
} from './siteUrl.js'

export function buildBreadcrumbList (items) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url ? absoluteUrl(item.url) : undefined
    }))
  }
}

export function buildWebPageNode ({ pathname, name, description }) {
  const pageUrl = absoluteUrl(pathname)
  return {
    '@type': 'WebPage',
    '@id': `${pageUrl}#webpage`,
    url: pageUrl,
    name,
    description,
    isPartOf: { '@id': getWebsiteSchemaId() },
    about: { '@id': getOrganizationSchemaId() },
    inLanguage: pathname.startsWith('/pt') ? 'pt-BR' : 'en-US'
  }
}

export function buildFaqPageNode (faqItems) {
  if (!Array.isArray(faqItems) || faqItems.length === 0) return null
  return {
    '@type': 'FAQPage',
    mainEntity: faqItems.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer
      }
    }))
  }
}

export function buildMedicalTherapyService ({ name, description, pathname }) {
  const pageUrl = absoluteUrl(pathname)
  return {
    '@type': 'MedicalTherapy',
    '@id': `${pageUrl}#service`,
    name,
    description,
    url: pageUrl,
    provider: { '@id': getOrganizationSchemaId() },
    areaServed: { '@type': 'State', name: 'Florida' },
    availableLanguage: ['English', 'Portuguese']
  }
}

export function buildContactPageGraph ({ pathname, name, description }) {
  const pageUrl = absoluteUrl(pathname)
  return {
    '@context': 'https://schema.org',
    '@graph': [
      buildWebPageNode({ pathname, name, description }),
      {
        '@type': 'ContactPage',
        '@id': `${pageUrl}#contact`,
        url: pageUrl,
        name,
        description,
        mainEntity: { '@id': getOrganizationSchemaId() }
      },
      buildBreadcrumbList([
        { name: 'Home', url: '/' },
        { name, url: pathname }
      ])
    ]
  }
}

/**
 * Locations / service-area page graph for local + AI search citation.
 * @param {{ pathname: string, pageName: string, pageDescription: string, faqItems?: Array<{question:string,answer:string}>, nearbyCities?: string[], counties?: string[] }} opts
 */
export function buildLocationsPageGraph ({
  pathname,
  pageName,
  pageDescription,
  faqItems = [],
  nearbyCities = [],
  counties = []
}) {
  const pageUrl = absoluteUrl(pathname)
  const areaServed = [
    { '@type': 'State', name: 'Florida' },
    { '@type': 'City', name: 'Boca Raton' },
    ...nearbyCities
      .filter((name) => name && name !== 'Boca Raton')
      .map((name) => ({ '@type': 'City', name })),
    ...counties
      .map((name) => String(name).replace(/\s*\(.*\)\s*$/, '').trim())
      .filter(Boolean)
      .map((name) => ({ '@type': 'AdministrativeArea', name }))
  ]

  const nodes = [
    buildWebPageNode({ pathname, name: pageName, description: pageDescription }),
    {
      '@type': 'MedicalBusiness',
      '@id': getOrganizationSchemaId(),
      name: 'VIVA Psychotherapy',
      url: getSiteUrl(),
      telephone: '+1-561-759-7519',
      email: 'info@vivapsychotherapy.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '5301 North Federal Highway Suite 200',
        addressLocality: 'Boca Raton',
        addressRegion: 'FL',
        postalCode: '33487',
        addressCountry: 'US'
      },
      areaServed,
      availableLanguage: ['English', 'Portuguese']
    },
    buildBreadcrumbList([
      { name: 'Home', url: '/' },
      { name: pageName, url: pathname }
    ])
  ]

  const faq = buildFaqPageNode(faqItems)
  if (faq) {
    faq['@id'] = `${pageUrl}#faq`
    nodes.push(faq)
  }

  return { '@context': 'https://schema.org', '@graph': nodes }
}

export function buildServicePageGraph ({
  pathname,
  pageName,
  pageDescription,
  serviceName,
  serviceDescription,
  faqItems,
  breadcrumbMiddle
}) {
  const nodes = [
    buildWebPageNode({ pathname, name: pageName, description: pageDescription }),
    buildMedicalTherapyService({
      name: serviceName,
      description: serviceDescription,
      pathname
    }),
    buildBreadcrumbList([
      { name: 'Home', url: '/' },
      ...(breadcrumbMiddle ? [breadcrumbMiddle] : []),
      { name: serviceName, url: pathname }
    ])
  ]
  const faq = buildFaqPageNode(faqItems)
  if (faq) nodes.push(faq)
  return { '@context': 'https://schema.org', '@graph': nodes }
}

export function buildJobPostingGraph ({ job, pathname, description }) {
  const pageUrl = absoluteUrl(pathname)
  const site = getSiteUrl()
  return {
    '@context': 'https://schema.org',
    '@graph': [
      buildWebPageNode({
        pathname,
        name: `${job.title} | VIVA Psychotherapy`,
        description
      }),
      {
        '@type': 'JobPosting',
        '@id': `${pageUrl}#job`,
        title: job.title,
        description: description || job.title,
        datePosted: job.date || undefined,
        hiringOrganization: {
          '@type': 'MedicalBusiness',
          '@id': getOrganizationSchemaId(),
          name: 'VIVA Psychotherapy',
          sameAs: site,
          logo: `${site}/img/logo/Color-logo-with-background-1024x1024.jpg`
        },
        jobLocation: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Boca Raton',
            addressRegion: 'FL',
            addressCountry: 'US'
          }
        },
        employmentType: 'CONTRACTOR',
        applicantLocationRequirements: {
          '@type': 'Country',
          name: 'United States'
        },
        url: pageUrl
      },
      buildBreadcrumbList([
        { name: 'Home', url: '/' },
        { name: 'Careers', url: '/careers' },
        { name: job.title, url: pathname }
      ])
    ]
  }
}
